import React, { useEffect, useRef } from 'react'
import { useMediaQuery } from 'react-responsive'

import { theme } from '@acorns/grove'
import {
  Money,
  getImageAsset,
  useAnalyticsClient,
  useRemoteStrings,
  StyledRemoteString,
} from '@acorns/web-utils'

import {
  trackRegistrationChooseSubscriptionComparePlansCloseButtonTappedPayload,
  trackRegistrationChooseSubscriptionComparePlansScreenViewedPayload,
  trackRegistrationChooseSubscriptionScreenSwipedPayload,
} from 'generated/trackingEvents'
import {
  BankingProduct,
  ProductKey,
  SubscriptionPricingContentQuery,
} from 'generated/types'
import { reopenFlowTypeAnalyticsProps } from 'utils/analytics/reopenFlowAnalytics'

import {
  CompareTable,
  CompareTitle,
  DividerCell,
  DividerLine,
  DoneButton,
  DrawerContent,
  Feature,
  FeatureDesc,
  FeatureTitle,
  FooterContainer,
  Header,
  HeaderLeft,
  Row,
  Section,
  StyledDrawer,
  StyledSeparator,
  TableWrapper,
} from './styled-components'
import { TierCell } from './TierCell'

interface Props {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  plans: NonNullable<
    SubscriptionPricingContentQuery['tierGroup']['tierOptions']
  >
  selectedTierIndex: number
  bankInterestRates: Array<{
    currentApy: string
    productName: BankingProduct
  }>
  eventFunnelValue: string
  isLongReopen?: boolean
}

// assuming 0-index is the highest tier since it's sorted in descending order
const DEFAULT_TIER_INDEX = 0

export const ComparePlansDrawer = (props: Props) => {
  const analytics = useAnalyticsClient()
  const { getRemoteString } = useRemoteStrings()
  const isMobile = useMediaQuery({
    maxWidth: `${theme.dimensions.breakpointsSmall}px`,
  })

  const bundleRefs = useRef<Record<string, HTMLElement>>({})
  const expectedBundleCount = useRef<number>(0)
  const observerRef = useRef<IntersectionObserver>()
  const viewedBundles = useRef<Set<string>>(new Set())

  const ctaTitle = getRemoteString({
    key: 'registration.tier_selection.compare.cta',
  })

  const selectedTier = props.plans?.[props.selectedTierIndex]
  const selectedTierPrice = selectedTier
    ? Money.toCompact(selectedTier.tier.tierPrice.amountV2.value).toString()
    : ''

  useEffect(() => {
    if (!props.isOpen || !props.plans?.length || !selectedTier) {
      return
    }

    expectedBundleCount.current = props.plans[0].tier.bundles.length
    viewedBundles.current.clear()

    analytics.track(
      ...trackRegistrationChooseSubscriptionComparePlansScreenViewedPayload({
        tierKey: selectedTier.tier.key,
        tierPrice: selectedTierPrice,
        funnel: props.eventFunnelValue,
        ...reopenFlowTypeAnalyticsProps(props.isLongReopen),
      }),
    )

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = undefined
      }
    }
  }, [
    props.isOpen,
    props.plans,
    selectedTier,
    selectedTierPrice,
    analytics,
    props.isLongReopen,
  ])

  const handleClose = () => {
    if (selectedTier) {
      analytics.track(
        ...trackRegistrationChooseSubscriptionComparePlansCloseButtonTappedPayload(
          {
            destination: 'registrationChooseSubscription',
            tierKey: selectedTier.tier.key,
            tierPrice: selectedTierPrice,
            ctaTitle,
            funnel: props.eventFunnelValue,
            ...reopenFlowTypeAnalyticsProps(props.isLongReopen),
          },
        ),
      )
    }
    props.setIsOpen(false)
  }

  // istanbul ignore next due to intersection callbacks not being testable
  const setupObserver = () => {
    if (!props.isOpen || !props.plans?.length || !selectedTier) {
      return
    }

    const numBundles = props.plans[0].tier.bundles?.length
    if (!numBundles) {
      return
    }

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return
        }

        const bundleName = Object.entries(bundleRefs.current).find(
          ([_, el]) => el === entry.target,
        )?.[0]
        if (!bundleName || viewedBundles.current.has(bundleName)) {
          return
        }

        const bundleIndex = props.plans[0].tier.bundles.findIndex(
          (bundle) => bundle.name === bundleName,
        )
        if (bundleIndex === -1) {
          return
        }

        viewedBundles.current.add(bundleName)

        const bundle = props.plans[0].tier.bundles[bundleIndex]
        analytics.track(
          ...trackRegistrationChooseSubscriptionScreenSwipedPayload({
            tierKey: selectedTier.tier.key,
            tierPrice: selectedTierPrice,
            indexPosition: bundleIndex,
            indexCount: numBundles,
            containerName: bundle.name,
            funnel: props.eventFunnelValue,
            ...reopenFlowTypeAnalyticsProps(props.isLongReopen),
          }),
        )
      })
    }

    const observer = new IntersectionObserver(observerCallback, {
      threshold: [1],
    })

    Object.values(bundleRefs.current).forEach((ref) => {
      if (ref) {
        observer.observe(ref)
      }
    })

    return observer
  }

  if (!props.plans || props.plans.length === 0) {
    return null
  }

  return (
    <StyledDrawer isOpen={props.isOpen} onClose={handleClose}>
      <DrawerContent>
        <CompareTitle>
          {getRemoteString({
            key: 'registration.tier_selection.compare.title',
          })}
        </CompareTitle>
        <TableWrapper>
          <CompareTable>
            <thead>
              <tr>
                <HeaderLeft>
                  {getRemoteString({
                    key: 'registration.tier_selection.compare.table_title',
                  })}
                </HeaderLeft>
                {props.plans.map((plan, planIndex) => (
                  <Header key={plan.tier.key} isGold={planIndex === 0}>
                    {plan.tier.name}
                  </Header>
                ))}
              </tr>
            </thead>
            <tbody>
              {props.plans[0].tier.bundles.map((bundle) => (
                <React.Fragment key={bundle.name}>
                  <Row>
                    <Section
                      ref={(el) => {
                        if (!el) {
                          return
                        }
                        bundleRefs.current[bundle.name] = el
                        if (
                          Object.keys(bundleRefs.current).length ===
                          expectedBundleCount.current
                        ) {
                          setupObserver()
                        }
                      }}
                    >
                      <img
                        src={getImageAsset(bundle.iconPath, 'svg').url}
                        alt="Product grouping"
                      />
                      {bundle.name}
                    </Section>
                    {props.plans.map((tierOption, planIndex) => (
                      <TierCell
                        key={`bundle-header-cell-${tierOption.tier.key}`}
                        tier={tierOption.tier}
                        isGold={planIndex === DEFAULT_TIER_INDEX}
                      />
                    ))}
                  </Row>
                  {bundle.products
                    .map((product, productIndex) =>
                      (product.descriptionKeysV2 ?? []).map(
                        (descriptionKey, descriptionKeyIndex) => {
                          const stringVariables = (() => {
                            switch (product.product.key) {
                              case ProductKey.EmergencyFund: {
                                const rate = props.bankInterestRates.find(
                                  (r) =>
                                    r.productName ===
                                    BankingProduct.EmergencySavings,
                                )
                                return rate ? [rate.currentApy] : []
                              }
                              case ProductKey.MightyOakCard: {
                                const rate = props.bankInterestRates.find(
                                  (r) =>
                                    r.productName ===
                                    BankingProduct.MightyOakChecking,
                                )
                                return rate ? [rate.currentApy] : []
                              }
                              default:
                                return []
                            }
                          })()
                          const isLast =
                            productIndex === bundle.products.length - 1 &&
                            descriptionKeyIndex ===
                              (product.descriptionKeysV2?.length ?? 0) - 1
                          return (
                            <React.Fragment
                              key={`bundle-description-${product.product.key}-${descriptionKeyIndex}`}
                            >
                              <Row isLast={isLast}>
                                <Feature>
                                  <FeatureTitle>
                                    {getRemoteString({
                                      key: descriptionKey.title,
                                    })}
                                  </FeatureTitle>
                                  <FeatureDesc>
                                    <StyledRemoteString
                                      stringKey={descriptionKey.subtitle}
                                      variables={stringVariables}
                                    />
                                  </FeatureDesc>
                                </Feature>
                                {props.plans.map((tierOption, planIndex) => (
                                  <TierCell
                                    key={`tier-product-${product.product.key}-${planIndex}-cell-${tierOption.tier.key}`}
                                    tier={tierOption.tier}
                                    currentProduct={product}
                                    isGold={planIndex === DEFAULT_TIER_INDEX}
                                  />
                                ))}
                              </Row>
                              {!isLast && (
                                <tr>
                                  <DividerCell colSpan={props.plans.length + 1}>
                                    <DividerLine />
                                  </DividerCell>
                                </tr>
                              )}
                            </React.Fragment>
                          )
                        },
                      ),
                    )
                    .reduce<React.ReactNode[]>(
                      (acc, items) => acc.concat(items),
                      [],
                    )}
                </React.Fragment>
              ))}
            </tbody>
          </CompareTable>
        </TableWrapper>
      </DrawerContent>
      {!isMobile && (
        <>
          <StyledSeparator />
          <FooterContainer>
            <DoneButton onClick={handleClose}>{ctaTitle}</DoneButton>
          </FooterContainer>
        </>
      )}
    </StyledDrawer>
  )
}
